import { motion } from 'framer-motion';
import type { Translate } from 'next-translate';
import React from 'react';
import { TextWithLinks } from '@/components/elements/TextWithLinks';
import { openCloseMotion } from '@/animations';
import ArrowIcon from '@/public/icons/arrow-bigger.svg';
import { type LandingListFAQ } from '../../../../../types';
import styles from './AccordionItem.module.scss';

interface Props {
	item: LandingListFAQ;
	index: number;
	expanded: number | null;
	toggle: (index: number) => void;
	t: Translate;
	/** Inline nodes for the answer's placeholders, keyed by the placeholder. */
	links?: Record<string, React.ReactNode>;
	variant?: 'default' | 'main';
}

export const AccordionItem: React.FC<Props> = ({
	item,
	index,
	expanded,
	toggle,
	t,
	links,
	variant = 'default',
}) => {
	const isOpen = index === expanded;
	const subtitle = t(item.subtitle);
	const renderText = (text: string) =>
		links ? <TextWithLinks text={text} parts={links} /> : text;

	return (
		<div
			key={`${item.title}_${index}`}
			className={`${styles['item']}${
				variant === 'main' ? ` ${styles['item_main']}` : ''
			}${variant === 'main' && isOpen ? ` ${styles['item_main-open']}` : ''}`}
			onClick={() => toggle(index)}
			role='button'
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') toggle(index);
			}}
			aria-expanded={isOpen}
			aria-controls={`accordion-content-${index}`}
		>
			<div
				className={styles['item__header']}
				aria-controls={`accordion-content-${index}`}
			>
				<h3 className={styles['item__title']}>{t(item.title)}</h3>
				<div className={styles['item__button']}>
					<span
						className={`${styles['item__button-icon']} ${
							isOpen ? styles['item__button-icon_open'] : ''
						}`}
					>
						<ArrowIcon
							className={`${styles['item__button-svg']} ${
								isOpen ? styles['item__button-svg_open'] : ''
							}`}
							aria-hidden='true'
							focusable='false'
						/>
					</span>
				</div>
			</div>

			<motion.div
				id={`accordion-content-${index}`}
				className={styles['item__content']}
				variants={openCloseMotion}
				initial={false}
				animate={isOpen ? 'open' : 'closed'}
			>
				<div className={styles['item__wrapper']}>
					<p className={styles['item__text']}>{renderText(subtitle)}</p>

					{item.blocks?.map(block => (
						<p
							key={block.text}
							className={`${styles['item__text']} ${
								block.heading ? styles['item__text_heading'] : ''
							}`}
						>
							{renderText(t(block.text))}
						</p>
					))}
				</div>
			</motion.div>
		</div>
	);
};
